import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";

const chapters = [
  {
    n: "01",
    it: { title: "L'origine", body: "Davide Perugini Supercar & Classic Cars nasce dalla passione di Davide per le vetture che hanno segnato la storia dell'automobile. Non un semplice commercio — una selezione." },
    en: { title: "The origin", body: "Davide Perugini Supercar & Classic Cars was born from Davide's passion for the cars that shaped automotive history. Not a mere business — a selection." },
  },
  {
    n: "02",
    it: { title: "La cura", body: "Ogni vettura viene scelta, valutata e presentata con la stessa attenzione che riserverei a un'auto della mia collezione personale. Trasparenza totale, professionalità e tanta passione." },
    en: { title: "The care", body: "Every car is chosen, appraised and presented with the same attention I'd give to a car in my personal collection. Total transparency, professionalism and true passion." },
  },
  {
    n: "03",
    it: { title: "Il servizio", body: "Dalla ricerca su misura alla consulenza per l'acquisto o la vendita, seguo personalmente ogni cliente." },
    en: { title: "The service", body: "From bespoke sourcing to advice on buying or selling, I personally look after every client." },
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay: i * 0.1, ease: [0.2, 0.8, 0.2, 1] } }),
};

export default function AboutManifesto() {
  const { lang } = useLang();
  return (
    <section
      id="chi-sono"
      className="relative py-32 sm:py-48 px-6 sm:px-10 lg:px-14 max-w-[1600px] mx-auto"
      data-testid="about-section"
    >
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="flex items-center gap-4 mb-14"
      >
        <span className="w-10 h-px bg-white/40" />
        <span className="label-eyebrow text-white/70">{lang === "en" ? "About" : "Chi sono"}</span>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
        <div className="lg:col-span-5">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-10"
          >
            <div className="relative overflow-hidden rounded-sm border border-white/10 p-8 sm:p-10 bg-[#0a0a0a]">
              <div className="label-eyebrow text-white/70 mb-3">{lang === "en" ? "Founder & CEO" : "Fondatore & CEO"}</div>
              <div className="font-serif text-4xl text-white mb-6">Davide Perugini</div>
              {lang === "en" ? (
                <>
                  <p className="text-neutral-200 leading-relaxed text-base italic">
                    «Some people remember their childhood through places.
                    <br />
                    I remember it through a sound: that of engines.
                    <br />
                    Growing up in Emilia-Romagna (Motor Valley) means breathing a culture of speed, innovation and passion.
                    <br />
                    That's where it all began.»
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-5">
                    I was born in a small town in Emilia-Romagna, a land where engines are not simply part of
                    the industry, but of the culture and of everyday life. Since childhood I grew up accompanied
                    by their sound. What for many was a simple melody became, for me, an endless source of
                    curiosity. That curiosity became a passion. The passion turned into a goal.
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-4">
                    I started from scratch, with great determination and the conviction that work, discipline
                    and perseverance can turn a vision into reality, building a path that has led me to run
                    what is today my company.
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-4">
                    For me it's not simply about buying or owning a car. It's about finding a car that tells a
                    story, that reflects a personality and that delivers emotion every time the engine comes to
                    life. It's a work of art capable of telling a story.
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-4">
                    Today I keep pursuing the same goal that has been with me since childhood: turning passion
                    into excellence and proving that dreams, when backed by expertise and true passion, can
                    become reality.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-neutral-200 leading-relaxed text-base italic">
                    «Ci sono persone che ricordano la propria infanzia attraverso i luoghi.
                    <br />
                    Io la ricordo attraverso un suono: quello dei motori.
                    <br />
                    Crescere in Emilia-Romagna (Motor Valley) significa respirare una cultura fatta di velocità, innovazione e passione.
                    <br />
                    È lì che è iniziato tutto.»
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-5">
                    Sono nato in un piccolo paese dell'Emilia-Romagna, una terra dove i motori non sono
                    semplicemente parte dell'industria, ma della cultura e della vita quotidiana.
                    Fin da bambino sono cresciuto accompagnato dal loro suono. Quella che per molti era una
                    semplice melodia, per me è diventata una fonte inesauribile di curiosità. Quella curiosità
                    è diventata una passione. La passione si è trasformata in un obbiettivo.
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-4">
                    Sono partito da zero, con tanta determinazione e la convinzione che il lavoro, la disciplina
                    e la perseveranza possano trasformare una visione in realtà, costruendo un percorso che mi
                    ha portato a guidare quella che oggi è la mia azienda.
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-4">
                    Per me non si tratta semplicemente di acquistare o possedere un'automobile. Si tratta di
                    trovare una vettura che racconta una storia, che rispecchia una personalità e che regala
                    emozioni ogni volta che il motore prende vita. È un'opera capace di raccontare.
                  </p>
                  <p className="text-neutral-400 leading-relaxed text-base mt-4">
                    Oggi continuo a perseguire lo stesso obiettivo che mi accompagna fin da bambino:
                    trasformare la passione in eccellenza e dimostrare che i sogni, quando sono sostenuti da
                    competenza e tanta passione, possono diventare realtà.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-7 space-y-20">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white"
          >
            Un dealer che parla<br />
            <span className="italic text-neutral-500">solo di ciò che ama.</span>
          </motion.h2>

          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="grid grid-cols-12 gap-6 items-start border-t border-white/10 pt-10"
              data-testid={`about-chapter-${c.n}`}
            >
              <div className="col-span-2 md:col-span-1">
                <span className="num-ornament text-5xl md:text-6xl">{c.n}</span>
              </div>
              <div className="col-span-10 md:col-span-11">
                <h3 className="font-serif text-2xl sm:text-3xl text-white mb-4">{c[lang].title}</h3>
                <p className="text-neutral-400 text-base sm:text-lg leading-relaxed max-w-2xl">{c[lang].body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
